import type { JSX } from 'preact';
import { offProps } from './OffNote.tsx';

export interface SegmentedOption<T extends string = string> {
  value: T;
  label: string;
  /**
   * Not open to this world yet (D52). The option takes D235's disabled look and is marked
   * `aria-disabled`; a press on it shows `reason` beside it and never calls `onChange`.
   */
  disabled?: boolean;
  /** Why the option cannot be chosen (D180), shown when it is pressed (D235). */
  reason?: string;
}

export interface SegmentedProps<T extends string = string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange?: (value: T) => void;
  /**
   * D458: `standalone` (the default) is a 48px track with 44px segments; `inRow` sits in a list
   * row, a 36px track with 32px segments, each with a 48px tap area (the segment's own button
   * is 48 tall, the 32px fill drawn inside it).
   */
  variant?: 'standalone' | 'inRow';
  size?: 'sm' | 'md';
  /** The control's height, overriding the variant's own (kept for existing call sites). */
  height?: number;
  /** The option the walkthrough is pointing at (D44), ringed in accent. */
  mark?: T;
  style?: JSX.CSSProperties;
}

/** The small control's height, so a thing placed under it (D233's compass) can read it. */
export const SEGMENTED_SM_H = 28;

/** D458: the two sizes' track heights, the segment inside each, and the in-row tap area. */
export const SEGMENTED_STANDALONE_H = 48;
export const SEGMENTED_INROW_H = 36;
export const SEGMENTED_INROW_TAP = 48;

/** Segmented control. options: [{value,label}] */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  variant = 'standalone',
  size = 'md',
  height,
  mark,
  style,
}: SegmentedProps<T>) {
  const inRow = variant === 'inRow' && !height && size !== 'sm';
  const h =
    height ?? (size === 'sm' ? SEGMENTED_SM_H : inRow ? SEGMENTED_INROW_H : SEGMENTED_STANDALONE_H);
  return (
    <div
      role="tablist"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        // A caller that lets the row scroll sideways (the Almanac's six branches) makes this
        // a scroll container, and a scroll container with no height of its own collapsed to
        // its padding and clipped the labels. The height is the control's, so it is set.
        minHeight: h,
        padding: 2,
        background: 'var(--surface-2)',
        borderRadius: 'var(--radius-md)',
        gap: 2,
        ...style,
      }}
    >
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={on}
            {...offProps(o.disabled === true, o.reason)}
            class={o.value === mark ? 'walk-mark' : undefined}
            onClick={o.disabled ? undefined : () => onChange?.(o.value)}
            style={{
              // In a row the button is the 48px tap area and the 32px segment is drawn by the
              // span inside it; the negative margin keeps the track at 36.
              height: inRow ? SEGMENTED_INROW_TAP : h - 4,
              margin: inRow ? `${-(SEGMENTED_INROW_TAP - (h - 4)) / 2}px 0` : undefined,
              // A caller that sets the height wants a target that size each way (D261).
              ...(height
                ? { minWidth: height - 4 }
                : !inRow && size !== 'sm'
                  ? { minWidth: h - 4 }
                  : {}),
              padding: inRow ? '0 2px' : '0 12px',
              border: 0,
              borderRadius: 3,
              // D171: a fill means chosen, so the selected option is the only live one
              // with one. A disabled option takes D235's grey box from base.css instead.
              background: !inRow && on ? 'var(--surface)' : 'transparent',
              color: 'var(--ink)',
              font: `${on ? 600 : 500} ${size === 'sm' || inRow ? 12 : 14}px/1 var(--font-ui)`,
              cursor: 'pointer',
              flex: '1 0 auto',
              transition: 'background var(--dur) var(--ease)',
            }}
          >
            {inRow ? (
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: h - 4,
                  padding: '0 10px',
                  borderRadius: 3,
                  background: on ? 'var(--surface)' : 'transparent',
                }}
              >
                {o.label}
              </span>
            ) : (
              o.label
            )}
          </button>
        );
      })}
    </div>
  );
}
